/**
 * VerifierStatusBanner — Phase 07.3 polish.
 *
 * Surfaces the Phase 04 post-deliberation verifier report on the review
 * panel. Collapsible — header shows pass/fail summary; expanding reveals
 * per-check detail with severity-aware styling.
 *
 * Lifted out of the inline `VerifierBanner` definition that lived inside
 * CalculationReviewPanel.tsx so:
 *   - The component can be unit-tested in isolation.
 *   - Other consumers (Phase 09 attorney overlay, Phase 08 home dashboard)
 *     can re-use the same surface.
 *   - The panel file shrinks below 800 LOC.
 *
 * Behavior unchanged from the inline version — pure refactor.
 */

import React, { useId, useState } from 'react';

import type { VerifierReport } from '../../../types/engineReview';

export interface VerifierStatusBannerProps {
  verifier?: VerifierReport;
}

export default function VerifierStatusBanner({ verifier }: VerifierStatusBannerProps) {
  const [expanded, setExpanded] = useState(false);
  // Unique per-instance id so multiple banners on the same page (e.g.
  // landing page with 12 engines) don't collide on aria-controls. Phase 07.8.
  const detailId = useId();

  if (!verifier) {
    return null;
  }

  // Backend marks checks_passed/checks_failed optional (VerifierReport schema);
  // real reports can omit them. Coalesce so the banner never reads a member off
  // undefined (prod crash 2026-08-24).
  const checksPassed = verifier.checks_passed ?? [];
  const checksFailed = verifier.checks_failed ?? [];

  const passedCount = checksPassed.length;
  const failedCount = checksFailed.length;
  const hasBlockers = checksFailed.some((c) => c.severity === 'blocker');
  const hasWarnings = checksFailed.some((c) => c.severity === 'warning');

  // Hue law (K3 review 2026-08-23): no translucent full-fill "glassy mass".
  // Neutral panel; the single hue marker is a 2px LEFT border keyed to severity
  // — crimson for blockers (adverse), a needs-human orange for warnings, and a
  // hueless verified accent when everything passed (verified reads quiet).
  let accent = 'var(--color-verified)';
  let iconStyle: React.CSSProperties = { color: 'var(--color-verified)' };
  let statusText = 'All checks passed';

  if (hasBlockers) {
    accent = 'var(--color-blocker)';
    iconStyle = { color: 'var(--color-blocker)' };
    statusText = `${failedCount} blocker${failedCount > 1 ? 's' : ''} found`;
  } else if (hasWarnings) {
    accent = 'var(--color-needs-human)';
    iconStyle = { color: 'var(--color-needs-human)' };
    statusText = `${failedCount} warning${failedCount > 1 ? 's' : ''}`;
  }

  const bannerStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border)',
    borderLeft: `2px solid ${accent}`,
  };

  return (
    <div
      className="rounded-[2px] p-3"
      style={bannerStyle}
      data-testid="verifier-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={expanded}
        aria-controls={detailId}
      >
        <div className="flex items-center gap-2">
          {hasBlockers ? (
            <svg className="w-5 h-5" style={iconStyle} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : hasWarnings ? (
            <svg className="w-5 h-5" style={iconStyle} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" style={iconStyle} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          <span className="text-sm font-medium">
            Verifier: {statusText}
          </span>
          {passedCount > 0 && (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              ({passedCount} passed)
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div id={detailId} className="mt-3 space-y-2">
          {checksFailed.map((check) => (
            <div
              key={check.check_id}
              className="p-2 rounded-[2px] text-sm"
              style={
                check.severity === 'blocker'
                  ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-blocker)', borderLeft: '2px solid var(--color-blocker)' }
                  : check.severity === 'warning'
                  ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderLeft: '2px solid var(--color-needs-human)' }
                  : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderLeft: '2px solid var(--color-border)' }
              }
              data-testid={`verifier-check-failed-${check.check_id}`}
            >
              <strong>{check.check_name}:</strong> {check.message}
            </div>
          ))}
          {checksPassed.length > 0 && (
            <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>Passed checks:</p>
              {checksPassed.map((check) => (
                <div
                  key={check.check_id}
                  className="text-xs flex items-center gap-1"
                  style={{ color: 'var(--color-verified)' }}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {check.check_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
